import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { TourStep, TourService, TourPlacement } from '../services/tour.service';

type Rect = { top: number; left: number; width: number; height: number };

@Component({
  selector: 'app-tour-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tour-overlay.component.html',
  styleUrl: './tour-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourOverlayComponent implements OnInit, OnDestroy {
  isOpen = false;

  step: TourStep | null = null;
  index = 0;
  total = 0;

  // highlight rect (viewport coords)
  highlight: Rect = { top: -9999, left: -9999, width: 0, height: 0 };

  // popover (viewport coords)
  popoverTop = -9999;
  popoverLeft = -9999;

  // Mask geometry (viewport coords)
  maskX = -9999;
  maskY = -9999;
  maskW = 0;
  maskH = 0;
  maskRx = 12;

  // Backdrop darkness
  overlayOpacity = 0.6;

  private sub = new Subscription();
  private ro: ResizeObserver | null = null;

  private readonly onAnyScroll = () => this.reposition();
  private readonly onAnyResize = () => this.reposition();

  constructor(private tour: TourService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Capture-phase scroll listener catches scroll in nested containers like .aroundMain
    document.addEventListener('scroll', this.onAnyScroll, true);
    window.addEventListener('resize', this.onAnyResize, { passive: true });

    this.sub.add(
      this.tour.vm$.subscribe(async vm => {
        this.isOpen = vm.isOpen;
        this.index = vm.index;
        this.total = vm.steps.length;
        this.step = vm.isOpen ? vm.steps[vm.index] : null;

        if (!this.isOpen || !this.step) {
          this.disconnectObservers();
          this.cdr.markForCheck();
          return;
        }

        if (this.step.beforeShow) await this.step.beforeShow();

        const el = await this.tour.waitForTarget(this.step.target, this.step.waitForTargetMs);
        if (!el) {
          if (this.index + 1 < this.total) await this.tour.next();
          else this.tour.close();
          return;
        }

        this.scrollTargetIntoView(el);
        this.updatePositions(el, this.step.placement ?? 'auto', this.step.padding ?? 8);
        this.observeTarget(el);

        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.disconnectObservers();

    document.removeEventListener('scroll', this.onAnyScroll, true);
    window.removeEventListener('resize', this.onAnyResize as any);
  }

  close() {
    this.tour.close();
  }

  async prev() {
    await this.tour.prev();
  }

  async nextOrDone() {
    if (this.index + 1 >= this.total) return this.tour.close();
    await this.tour.next();
  }

  @HostListener('window:keydown', ['$event'])
  async onKeydown(e: KeyboardEvent) {
    if (!this.isOpen) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      await this.prev();
      return;
    }
    if (e.key === 'ArrowRight' || e.key === 'Enter') {
      e.preventDefault();
      await this.nextOrDone();
      return;
    }
  }

  private reposition() {
    if (!this.isOpen || !this.step) return;

    const el = document.querySelector(this.step.target) as HTMLElement | null;
    if (!el) return;

    this.updatePositions(el, this.step.placement ?? 'auto', this.step.padding ?? 8);
    this.cdr.markForCheck();
  }

  private observeTarget(el: HTMLElement) {
    this.disconnectObservers();
    this.ro = new ResizeObserver(() => this.reposition());
    this.ro.observe(el);
  }

  private disconnectObservers() {
    if (this.ro) {
      try {
        this.ro.disconnect();
      } catch {}
      this.ro = null;
    }
  }

  private scrollTargetIntoView(el: HTMLElement) {
    const rect = el.getBoundingClientRect();
    const margin = 90;

    const inView =
      rect.top >= margin &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight - margin &&
      rect.right <= window.innerWidth;

    if (inView) return;

    const scroller = this.findScrollableAncestor(el);
    if (!scroller) {
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      return;
    }

    const scrollerRect = scroller.getBoundingClientRect();
    const elTopWithinScroller = rect.top - scrollerRect.top + scroller.scrollTop;
    const targetScrollTop = Math.max(0, elTopWithinScroller - scroller.clientHeight / 2);

    scroller.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
  }

  private findScrollableAncestor(el: HTMLElement): HTMLElement | null {
    let cur: HTMLElement | null = el.parentElement;

    while (cur && cur !== document.body) {
      const style = getComputedStyle(cur);
      const oy = style.overflowY;
      const ox = style.overflowX;

      const scrollableY =
        (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
        cur.scrollHeight > cur.clientHeight;

      const scrollableX =
        (ox === 'auto' || ox === 'scroll' || ox === 'overlay') &&
        cur.scrollWidth > cur.clientWidth;

      if (scrollableY || scrollableX) return cur;
      cur = cur.parentElement;
    }

    return null;
  }

  /**
   * Viewport-based positioning (highlight + popover are position: fixed)
   * Also updates SVG mask cutout coords.
   */
  private updatePositions(el: HTMLElement, placement: TourPlacement, pad: number) {
    const r = el.getBoundingClientRect();

    const hlRemember: Rect = {
      top: Math.round(r.top - pad),
      left: Math.round(r.left - pad),
      width: Math.round(r.width + pad * 2),
      height: Math.round(r.height + pad * 2),
    };

    this.highlight = hlRemember;

    // Update mask cutout (same as highlight box)
    this.maskX = hlRemember.left;
    this.maskY = hlRemember.top;
    this.maskW = hlRemember.width;
    this.maskH = hlRemember.height;
    this.maskRx = 12;

    // Popover placement (viewport coords)
    const popW = Math.min(380, window.innerWidth - 24);
    const popH = 210;
    const gap = 12;

    const candidates = {
      top: { top: r.top - popH - gap, left: r.left },
      right: { top: r.top, left: r.left + r.width + gap },
      bottom: { top: r.top + r.height + gap, left: r.left },
      left: { top: r.top, left: r.left - popW - gap },
    };

    const order: Array<Exclude<TourPlacement, 'auto'>> =
      placement === 'auto' ? ['right', 'bottom', 'left', 'top'] : [placement as any];

    let pos = candidates[order[0]];
    for (const p of order) {
      const c = candidates[p];
      if (this.fitsInViewport(c.top, c.left, popW, popH)) {
        pos = c;
        break;
      }
    }

    const minLeft = 12;
    const minTop = 12;
    const maxLeft = window.innerWidth - popW - 12;
    const maxTop = window.innerHeight - popH - 12;

    this.popoverLeft = Math.round(Math.max(minLeft, Math.min(pos.left, maxLeft)));
    this.popoverTop = Math.round(Math.max(minTop, Math.min(pos.top, maxTop)));
  }

  private fitsInViewport(t: number, l: number, w: number, h: number) {
    return l >= 0 && t >= 0 && l + w <= window.innerWidth && t + h <= window.innerHeight;
  }
}
